import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import { getErrorMessage } from '@/shared/utils/handle-api-error'
import { firstRunResetSummary } from '../models/demo-first-run'
import { demoFirstRunV2Service } from '../services/demo-first-run-v2.service'

/**
 * El estado de «primer ingreso» de UNA cuenta. Sólo responde 200 para un visor de demo sobre la
 * cuenta de revisión; para el resto es un 404/403 que la tarjeta lee como «no me dibujo».
 *
 * Sin reintentos ni toast: un 404 es la respuesta normal en el 99 % de las cuentas.
 */
export function useDemoFirstRunStatusQuery(id: string, enabled: boolean) {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  return useQuery({
    queryKey: queryKeys.clientAccounts.demoFirstRunV2(id),
    queryFn: async () => await demoFirstRunV2Service.status(accessToken, id),
    enabled: enabled && Boolean(accessToken) && Boolean(id),
    refetchOnWindowFocus: false,
    retry: false
  })
}

export function useDemoFirstRunReset() {
  const { auth } = useAuth()
  const queryClient = useQueryClient()
  const accessToken = auth?.v2AccessToken ?? ''

  return useMutation({
    mutationFn: async (id: string) => await demoFirstRunV2Service.reset(accessToken, id),
    onSuccess: (result) => {
      // La familia entera: el estado de la tarjeta, el detalle y los dispositivos (las sesiones
      // cerradas) cuelgan de `clientAccounts.all`.
      void queryClient.invalidateQueries({ queryKey: queryKeys.clientAccounts.all })
      toast.success(firstRunResetSummary(result))
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}
